import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiClient, setAuthToken, setUnauthorizedHandler } from "../api/client";

const AuthContext = createContext(null);

const TOKEN_KEY = "vizaura_token";
const USER_KEY = "vizaura_user";

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Hydrate axios with whatever's in storage on boot.
  useEffect(() => {
    setAuthToken(token);
  }, [token]);

  // Refresh the user (incl. is_admin) from the server on boot, so role changes
  // take effect without needing to log out and back in.
  useEffect(() => {
    if (!token) return;
    apiClient
      .get("/api/auth/me")
      .then((res) => {
        setUser(res.data);
        localStorage.setItem(USER_KEY, JSON.stringify(res.data));
      })
      .catch(() => {
        /* token invalid/expired — leave state; user can re-auth */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = useCallback((newToken, newUser) => {
    setToken(newToken);
    setUser(newUser);
    if (newToken) localStorage.setItem(TOKEN_KEY, newToken);
    else localStorage.removeItem(TOKEN_KEY);
    if (newUser) localStorage.setItem(USER_KEY, JSON.stringify(newUser));
    else localStorage.removeItem(USER_KEY);
    setAuthToken(newToken);
  }, []);

  // When any authenticated request 401s (token expired/invalid), sign the user
  // out cleanly and flag it so the login screen can explain what happened —
  // instead of surfacing a raw "Token expired" error mid-action.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setSessionExpired(true);
      persist(null, null);
    });
    return () => setUnauthorizedHandler(null);
  }, [persist]);

  const login = useCallback(
    async (identifier, password) => {
      setLoading(true);
      try {
        const res = await apiClient.post("/api/auth/login", { identifier, password });
        persist(res.data.access_token, res.data.user);
        setSessionExpired(false);
        return res.data.user;
      } finally {
        setLoading(false);
      }
    },
    [persist],
  );

  const register = useCallback(
    async (email, username, password) => {
      setLoading(true);
      try {
        const res = await apiClient.post("/api/auth/register", { email, username, password });
        persist(res.data.access_token, res.data.user);
        setSessionExpired(false);
        return res.data.user;
      } finally {
        setLoading(false);
      }
    },
    [persist],
  );

  const logout = useCallback(() => {
    persist(null, null);
  }, [persist]);

  const forgotPassword = useCallback(async (email) => {
    const res = await apiClient.post("/api/auth/forgot-password", { email });
    return res.data;
  }, []);

  const resetPassword = useCallback(async (resetToken, newPassword) => {
    const res = await apiClient.post("/api/auth/reset-password", {
      token: resetToken,
      new_password: newPassword,
    });
    return res.data;
  }, []);

  const value = useMemo(
    () => ({ token, user, loading, sessionExpired, login, register, logout, forgotPassword, resetPassword }),
    [token, user, loading, sessionExpired, login, register, logout, forgotPassword, resetPassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
